// Factory replacing the 8 copy-pasted sortaballs puzzle loaders. The loader
// parses the variant's generator JSON once at module load (stable references
// per id) and exposes the shared getPuzzleById / getTotalPuzzleCount surface.
// Each variant's `puzzles.ts` is now a single factory call.

import type { GameState, Move } from "./logic"

import type { SortaballsPuzzle } from "./sortaballs-variant"

// Raw generator-JSON shape (cast at the boundary). Fields are optional so the
// strictest (base, required date/level) and loosest (high-spread, optional)
// shapes share one interface; the loader falls back to "" where absent.
export interface SortaballsJsonEntry {
  date?: string
  // The generator's stored sort key (10-colors+): difficultyScore reads this
  // directly; entries without it fall back to the formula below (legacy).
  difficulty?: number
  effort?: number
  game: {
    correctMoves?: { from: number; to: number }[]
    moveLimits?: { down: number; up: number }
    solutionLength: number
    state: string[][]
    // Base only: fallback effort = log10(statesExplored) when the entry ships
    // no top-level effort (read by the base variant's resolveEffort).
    statesExplored?: number
  }
  id?: string
  level?: string
  numberOfMoves: number
  trapDensity?: number
}

interface CreatePuzzleLoaderInput {
  // The variant's JSON array (cast to SortaballsJsonEntry[] at the boundary).
  data: unknown
  // Present ⇒ the variant ships effort props: difficultyScore = the entry's
  // stored `difficulty` sort key when it has one, else effort + 2*trapDensity
  // (the legacy formula), plus effort + trapDensity on each puzzle. Absent
  // (9-plates/spread/high-spread) ⇒ those fields stay absent.
  resolveEffort?: (entry: SortaballsJsonEntry)=> number | undefined
  // Sort the set easiest-first (same order as the generators) and re-assign
  // ids so id = index + 1 after the sort.
  sort?: boolean
}

export function createPuzzleLoader(input: CreatePuzzleLoaderInput) {
  const { data, resolveEffort, sort = false } = input
  const hasEffort = resolveEffort !== undefined

  // SAFETY: data is a JSON array of SortaballsJsonEntry
  let puzzles: SortaballsPuzzle[] = (data as SortaballsJsonEntry[]).map(
    (entry, index)=> {
      const effort = hasEffort ? resolveEffort(entry) : undefined
      return {
        boardId: entry.id ?? "",
        correctMoves: entry.game.correctMoves ?? [],
        date: entry.date ?? "",
        difficultyScore:
          entry.difficulty ??
          (effort === undefined ? undefined : effort + 2 * (entry.trapDensity ?? 0)),
        effort,
        id: index + 1,
        level: entry.level ?? "",
        moveLimits: entry.game.moveLimits ?? {
          down: entry.numberOfMoves,
          up: entry.numberOfMoves,
        },
        solutionLength: entry.numberOfMoves,
        // SAFETY: puzzle JSON state conforms to GameState
        state: entry.game.state as GameState,
        trapDensity: hasEffort ? entry.trapDensity : undefined,
      }
    },
  )

  if (sort) {
    puzzles = puzzles
      .sort(
        (a, b)=>
          (a.difficultyScore ?? Number.POSITIVE_INFINITY) -
            (b.difficultyScore ?? Number.POSITIVE_INFINITY) ||
          a.solutionLength - b.solutionLength,
      )
      .map((puzzle, index)=> ({ ...puzzle, id: index + 1 }))
  }

  // ids are dense 1..N (assigned as index + 1 above / after the sort), so a
  // direct index read beats a linear scan — getPuzzleById runs several times
  // per render in the game shell.
  const getPuzzleById = (id: number): SortaballsPuzzle | undefined =>
    puzzles[id - 1]

  const getTotalPuzzleCount = (): number=> puzzles.length

  return { getPuzzleById, getTotalPuzzleCount }
}